import { Box, Button, DialogActions, DialogContent, Grid, Stack, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import convertCurrency, { convertIntMoney } from "src/helper/convertCurrency";
import OrderTable from "../all-order/OrderTable";
import MallScanner from "src/components/shared/ScanningInput";
import WaitListTable from "../all-order/WaitListTable";
import useOrderDetail from "src/hooks/orders/useOrderDetail";
import useAuthGlobal from "src/context/useAuthGlobal";
import useOrderTrackingStatus from "src/hooks/orders/useOrderTrackingStatus";
import { orderStatusFormatted } from "src/constant";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import useDebounce from "src/hooks/useDebounce";
import useOrderScan from "src/hooks/orders/useOrderScan";

export default function PrepareBulkOrderScan({ order, mutate: parentMutate }) {
  const { userMe } = useAuthGlobal();
  const [itemCodes, setItemCodes] = useState();
  const [waitList, setWaitList] = useState();
  const debounceCode = useDebounce(itemCodes, 200);

  const { trigger: confirm } = useOrderTrackingStatus(order?.data?._id);
  const { data: orderDetail, mutate: refreshOrderDetail } = useOrderDetail(order?.data?._id, { warehouse: userMe?.currentWarehouse?._id, id: order?.data?._id });
  const { trigger: scan } = useOrderScan(order?.data?._id);

  const totalProd = Array.isArray(orderDetail?.data?.orderDetails) ? orderDetail?.data?.orderDetails?.reduce((acc, curr) => acc + curr?.qty, 0) : 0;

  const handleConfirm = async () => {
    const res = await confirm({
      body: {
        trackingStatus: orderStatusFormatted.ReadyToPick,
      },
    });
    if (res?.success) {
      //   const code = activeOrder?.code ?? "";
      parentMutate();
      enqueueSnackbar(`Đã soạn xong đơn . Tiếp tục scan mã vận đơn khác.`, { variant: "success", autoHideDuration: 5000 });
      // resetToAwb();
      return;
    }
    enqueueSnackbar(res?.error?.message ?? "Không xác nhận được đơn", { variant: "error" });
  };

  useEffect(() => {
    const handleScan = async () => {
      const code = waitList[0];
      if (!code || !order?.data?._id) return;

      const res = await scan({
        body: { itemCode: code },
      });

      if (res?.success) {
        setWaitList((prev) => prev.slice(1));
        refreshOrderDetail();
      } else {
        enqueueSnackbar(
          <span>
            {res?.error?.message} <b>{code}</b>
          </span>,
          {
            variant: "warning",
            persist: true,
            action: (snackbarId) => (
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  setWaitList((prev) => prev.slice(1));
                  closeSnackbar(snackbarId);
                }}
              >
                Xác nhận
              </Button>
            ),
          },
        );
      }
    };

    if (waitList?.length > 0) {
      handleScan();
    }
  }, [waitList?.length, order?.data?._id, scan, refreshOrderDetail]);

  useEffect(() => {
    if (debounceCode) {
      const refinedArray = debounceCode?.map((v) => v?.replaceAll("Shift", ""));
      setWaitList(refinedArray);
      setItemCodes(null);
    }
  }, [debounceCode]);

  const isFinished = () => {
    const details = orderDetail?.data?.orderDetails;
    if (!Array.isArray(details)) return false;
    const totalOrder = details.reduce((acc, curr) => acc + curr?.qty, 0);
    const currTotal = details.reduce((acc, curr) => acc + (curr?.pickedQty || 0), 0);
    return totalOrder > 0 && totalOrder === currTotal;
  };

  return (
    <>
      <DialogContent>
        <Grid container>
          <Grid
            xs={12}
            item
            display="flex"
          >
            <Box
              flex={1}
              display="flex"
              flexDirection="column"
              sx={{
                background: "var(--light-grey)",
                p: "6px 12px",
                borderRadius: "6px",
              }}
            >
              <Typography
                sx={{ fontSize: "16px" }}
                fontWeight="bold"
              >
                Thông tin khách hàng
              </Typography>
              <Typography
                mt="6px"
                variant="caption"
              >
                <strong>Tên KH: </strong>
                {order?.data?.customer?.fullName ?? "N/A"}
              </Typography>
              <Typography
                variant="caption"
                mt="4px"
              >
                <strong>SĐT: </strong>
                {order?.data?.customer?.phone ?? "N/A"}
              </Typography>
              <Typography
                variant="caption"
                mt="4px"
              >
                <strong>Địa chỉ: </strong>
                {order?.data?.recipient?.detailAddress ?? "N/A"}
              </Typography>
            </Box>
            <Box
              flex={1}
              display="flex"
              flexDirection="column"
              sx={{
                background: "var(--light-grey)",
                p: "6px 12px",
                borderRadius: "6px",
              }}
            >
              <Typography
                sx={{ fontSize: "16px" }}
                fontWeight="bold"
              >
                Thông tin đơn hàng
              </Typography>
              <Typography
                mt="6px"
                variant="caption"
              >
                <strong>Mã đơn hàng: </strong>
                {order?.data?.code ?? "N/A"}
              </Typography>
              <Typography
                variant="caption"
                mt="4px"
              >
                <strong>Tổng số lượng sản phẩm: </strong>
                {totalProd ?? 0}
              </Typography>
              <Typography
                mt="4px"
                variant="caption"
              >
                <strong>Tổng giá trị: </strong>
                {convertIntMoney(orderDetail?.data?.subTotal ?? 0)} đồng
                <br />
                <Typography
                  component="span"
                  variant="caption"
                >
                  ({convertCurrency(orderDetail?.data?.subTotal ?? 0)} đồng)
                </Typography>
              </Typography>
            </Box>
          </Grid>
          <Grid
            container
            spacing={2}
          >
            <Grid
              xs={8}
              item
            >
              <Stack
                gap={2}
                mt={1}
              >
                <Typography
                  fontWeight="bold"
                  fontSize="16px"
                >
                  Danh sách sản phẩm
                </Typography>
                <OrderTable prodList={orderDetail?.data?.orderDetails} />
              </Stack>
            </Grid>
            <Grid
              xs={4}
              item
              mt={5.5}
            >
              <Stack
                gap={2}
                mt={1}
              >
                <MallScanner
                  // key={`prod-${scanSession}-${orderId}`}
                  getIndividualValue={setItemCodes}
                  individualValue={itemCodes}
                  label="Scan mã sản phẩm"
                />
                <WaitListTable list={waitList} />
              </Stack>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: "0px 12px 24px" }}>
        <Button
          onClick={handleConfirm}
          disabled={!isFinished()}
          fullWidth
          variant="contained"
        >
          Hoàn thành soạn hàng
        </Button>
      </DialogActions>
    </>
  );
}
